import _ from 'lodash'

import React from 'react'

import { BasicViewProps } from 'constants/models'
import { MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'
import { ADMINISTRATION_STATUS_LABELS, ADMINISTRATION_TYPE } from 'services/administrations/constants'
import { Administration, EmergencyDose } from 'services/administrations/models'

import { Avatar, Callout, DetailsBreakdown, Page, Spinner, TimeString, Typography } from 'components'
import { SignaturePreview } from 'module/Safeguarding/components'
import PreviewStatus from 'module/Safeguarding/components/PreviewStatus'

import i18n from 'translations'

import { DISPLAY_SHORT_DATE_FORMAT } from 'constants/date'
import moment from 'moment'
import { ADMINISTRATION_STATUS_COLORS } from './constants'
import { StyledSignatureValueContainer } from './PreviewStyle'

interface PreviewViewProps extends BasicViewProps {
  administrations?: Administration & EmergencyDose
}

const AddView: React.FC<PreviewViewProps> = ({
  administrations,
  errorMessages,
  isLoading,
}) => {
  const {
    child: rootChild,
    medicationForm,
    otherNotes,
    signatures,
    status,
    takenAt,
    timeOfAdministration,
    type,
  } = administrations || {}
  const { child: medicationFormChild, medicationDetails } = medicationForm || {}
  const {
    dose,
    expiryDate,
    medication,
    methodOfAdministration,
    reason,
    sideEffects,
  } = medicationDetails || administrations?.medicationDetails || {}

  const finalChild = rootChild || medicationFormChild

  const witness = _.find(signatures, { type: MF_SIGNATURE_TYPES.WITNESS })
  const administeredBy = _.find(signatures, { type: MF_SIGNATURE_TYPES.STAFF })
  const parent = _.find(signatures, { type: MF_SIGNATURE_TYPES.PARENT })

  const renderBreakdownItem = ({ label, padding = '0 0 15px 0', value }) => (
    <DetailsBreakdown.Item
      label={label}
      margin="0"
      padding={padding}
      value={value || '-'}
      withoutBorder
    />
  )

  const renderSignatureDetails = () => (
    <React.Fragment>
      <Typography fontSize={20} margin="15px 0" bold>
        {i18n.t('module:Safeguarding:Administration:Preview:Headers:Signature:title')}
      </Typography>
      {administeredBy && (
        <React.Fragment>
          <Typography fontSize={18} margin="15px 0" bold>
            {i18n.t('global:Staff')}
          </Typography>
          {renderBreakdownItem({
            label: i18n.t('module:Safeguarding:Administration:Preview:Headers:Signature:administeredBy'),
            value: (
              <StyledSignatureValueContainer>
                <div>
                  <Avatar
                    avatarSize="small"
                    initials={administeredBy.owner.name.split(' ')}
                    src={administeredBy.owner.photo}
                    title={administeredBy.owner.name}
                  />
                </div>
                <SignaturePreview signedAt={administeredBy.signedAt} url={administeredBy.signature.url} />
              </StyledSignatureValueContainer>
            ),
          })}
        </React.Fragment>
      )}
      {witness && renderBreakdownItem({
        label: _.upperFirst(i18n.t('module:Safeguarding:Common:witness')),
        value: (
          <StyledSignatureValueContainer>
            <div>
              <Avatar
                avatarSize="small"
                initials={witness.owner.name.split(' ')}
                src={witness.owner.photo}
                title={witness.owner.name}
              />
            </div>
            <SignaturePreview signedAt={witness.signedAt} url={witness.signature.url} />
          </StyledSignatureValueContainer>
        ),
      })}
      {parent && (
        <React.Fragment>
          <Typography fontSize={18} margin="15px 0" bold>
            {i18n.t('global:Parent')}
          </Typography>
          {renderBreakdownItem({
            label: i18n.t('global:Parent'),
            padding: '0',
            value: (
              <StyledSignatureValueContainer>
                <div>
                  <Avatar
                    avatarSize="small"
                    initials={[parent.carer.firstName, parent.carer.surname]}
                    src={parent.carer.photo}
                    title={`${parent.carer.firstName} ${parent.carer.surname}`}
                  />
                </div>
                <SignaturePreview signedAt={parent.signedAt} url={parent.signature.url} />
              </StyledSignatureValueContainer>
            ),
          })}
        </React.Fragment>
      )}
    </React.Fragment>
  )

  const renderMedicationDetails = () => (
    <React.Fragment>
      <Typography fontSize={20} margin="15px 0" bold>
        {i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:title')}
      </Typography>
      <DetailsBreakdown>
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:reason'),
          value: reason,
        })}
        {renderBreakdownItem({
          label: i18n.t(
            'module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:methodOfAdministration',
          ),
          value: methodOfAdministration,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:dose'),
          value: dose,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:administrationTime'),
          value: <TimeString date={takenAt as Date} />,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:expiryDate'),
          value: expiryDate ? moment(expiryDate).format(DISPLAY_SHORT_DATE_FORMAT) : null,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:sideEffects'),
          value: sideEffects,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:notes'),
          value: otherNotes,
        })}
      </DetailsBreakdown>
    </React.Fragment>
  )

  const renderContent = () => {
    if (isLoading || !administrations) {
      return <Spinner />
    }

    return (
      <React.Fragment>
        <DetailsBreakdown>
          <DetailsBreakdown.Item center={false} margin="0" padding="0 0 15px 0" withoutBorder>
            <Avatar
              avatarSize="medium_big"
              initials={[finalChild.firstName, finalChild.surname]}
              src={finalChild.photo}
              title={(
                <Typography fontSize={18} bold>
                  {finalChild.firstName}
                  {' '}
                  {finalChild.surname}
                </Typography>
              )}
            />
          </DetailsBreakdown.Item>
          {renderBreakdownItem({
            label: i18n.t('module:Safeguarding:Administration:Preview:Headers:medication'),
            value: medication,
          })}
          {renderBreakdownItem({
            label: i18n.t('module:Safeguarding:Administration:Preview:Headers:dose'),
            value: dose,
          })}
          {renderBreakdownItem({
            label: i18n.t('global:Time'),
            value: timeOfAdministration
              ? <TimeString date={timeOfAdministration} local={false} />
              : <TimeString date={takenAt as Date} />,
          })}
          {renderBreakdownItem({
            label: i18n.t('module:Safeguarding:Administration:Preview:Headers:otherNotes'),
            value: otherNotes,
          })}
        </DetailsBreakdown>
        {type === ADMINISTRATION_TYPE.EMERGENCY && renderMedicationDetails()}
        {renderSignatureDetails()}
      </React.Fragment>
    )
  }

  return (
    <Page.Section
      actions={(
        <PreviewStatus
          badgeColor={ADMINISTRATION_STATUS_COLORS[status]}
          statusLabel={ADMINISTRATION_STATUS_LABELS[status]}
        />
      )}
      title={
        type === ADMINISTRATION_TYPE.SCHEDULED
          ? i18n.t('module:Safeguarding:Administration:Preview:title')
          : i18n.t('module:Safeguarding:Administration:Preview:emergencyTitle')
      }
    >
      <Callout content={errorMessages} error />
      {renderContent()}
    </Page.Section>
  )
}

export default AddView
